/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { API, Storage } from "aws-amplify";
import { Image } from "@aws-amplify/ui-react";
import {
  ArrowUpOnSquareIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { deletePhoto } from "src/graphql/mutations";
import { queryPhotos } from "src/services/AmplifyQuery";

import Layout from "src/components/layouts/Layout.react";
import Card from "src/components/cards/Card.react";
import LoadingCard from "src/components/loading/LoadingCard.react";
import InspectionInfo from "src/components/cards/InspectionInfo.react";
import {
  Table,
  TableBody,
  Col,
} from "src/components/tables/InspectionTable.react";

import PhotoUpload from "src/components/slideovers/PhotoUpload.react";
import EditCaption from "src/components/modals/EditCaption.react";
import RemoveModal from "src/components/modals/RemoveModal.react";

/**
 * The Photo Page component
 *
 * @author Thais Watanabe
 */
const Photo = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    turbineInspection,
    selectedItem,
    result,
    inspection,
    lastTurbineInspections,
  } = location.state;

  const [photos, setPhotos] = useState(null);
  const [photosURL, setPhotosURL] = useState(null);

  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const [isSlideOpen, setIsSlideOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isEditCaptionOpen, setIsEditCaptionOpen] = useState(false);

  window.onpopstate = () => {
    navigate(`/report/${selectedItem.modelNumber}#${selectedItem.name}`, {
      state: {
        inspection: inspection,
        turbineInspection: turbineInspection,
        lastTurbineInspections: lastTurbineInspections,
      },
    });
  };

  /* useEffect */
  useEffect(() => {
    fetchPhotos();

    return () => {
      setPhotos([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchImages() {
      if (photos) {
        const urls = await Promise.all(
          photos?.map(async (obj) => await Storage.get(obj?.s3Path.slice(7)))
        );
        setPhotosURL(urls);
      }
    }

    fetchImages();
  }, [photos]);

  /**
   * fetchPhotos
   */
  async function fetchPhotos() {
    const filter = {
      modelItemName: { beginsWith: selectedItem.name },
      turbineInspectionID: { eq: turbineInspection.id },
    };

    queryPhotos(filter, null, [], setPhotos);
  }

  /**
   * openRemoveDialog
   * @param {*} photo
   */
  async function openRemoveDialog(photo) {
    setSelectedPhoto(photo);
    setIsRemoveModalOpen(true);
  }

  /**
   * closeRemoveDialog
   */
  async function closeRemoveDialog() {
    setSelectedPhoto(null);
    setIsRemoveModalOpen(false);
  }

  /**
   * showEditCaption
   */
  function showEditCaption(photo) {
    setSelectedPhoto(photo);
    setIsEditCaptionOpen(!isEditCaptionOpen);
  }

  /**
   * removePhoto
   */
  async function removePhoto() {
    const photoToRemove = {
      id: selectedPhoto.id,
    };

    var s3Path = selectedPhoto.s3Path.substring(
      selectedPhoto.s3Path.indexOf("/") + 1
    );

    try {
      // Delete from S3
      await Storage.remove(s3Path);

      // Delete from DB
      await API.graphql({
        query: deletePhoto,
        variables: { input: photoToRemove },
      });

      fetchPhotos();
      closeRemoveDialog();
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * openEdit
   */
  function openEdit(photo) {
    photo && setSelectedPhoto(photo);
    setIsSlideOpen(!isSlideOpen);
  }

  return (
    <Layout
      title={turbineInspection.name}
      breadcrumbs={["登録済点検一覧", "登録済点検一覧（号機）", "結果", "写真"]}
    >
      <InspectionInfo turbineInspection={turbineInspection} />

      <h3 className="text-3xl	my-8">
        {`${selectedItem.name} - ${selectedItem.description}`}
        <button
          type="button"
          className="mx-4 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
          onClick={() => openEdit()}
        >
          {/* <!-- Heroicon name: upload --> */}
          <ArrowUpOnSquareIcon className="-ml-0.5 mr-2 h-4 w-4" />
          写真アップロード
        </button>
      </h3>

      {!photos ? (
        <LoadingCard />
      ) : (
        photos
          .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
          .map((photo, i) => (
            <Card key={i}>
              <Table className="card-table table-vcenter text-nowrap">
                <TableBody>
                  <tr>
                    <Col
                      className="w-2/4"
                      rowSpan={
                        turbineInspection.windTurbine.tower === 1 ? "5" : "4"
                      }
                    >
                      <Image src={photosURL?.[i]} />
                    </Col>
                    <Col>
                      <span className="font-bold">日付</span>
                    </Col>
                    <Col>
                      {moment(photo.createdAt).format("YYYY-MM-DD hh:mm")}
                    </Col>
                  </tr>
                  <tr>
                    <Col>
                      <span className="font-bold">処置進度</span>
                    </Col>
                    <Col>
                      {photo.type === "confirmation" && "確認"}
                      {photo.type === "before" && "前"}
                      {photo.type === "after" && "後"}
                      {photo.type === "handwritten" && "手書き"}
                    </Col>
                  </tr>
                  <tr>
                    <Col>
                      <span className="font-bold">キャプション</span>
                      <button
                        onClick={() => showEditCaption(photo)}
                        className="rounded-full items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                      >
                        {/* <!-- Heroicon name: pencil-alt --> */}
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                    </Col>
                    <Col>{photo.caption}</Col>
                  </tr>
                  <tr>
                    <Col colSpan="2">
                      <button
                        onClick={() => openEdit(photo)}
                        type="button"
                        className="inline-flex items-center bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                      >
                        <PencilSquareIcon
                          className="-ml-0.5 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                        修正
                      </button>

                      <button
                        onClick={() => openRemoveDialog(photo)}
                        type="button"
                        className="inline-flex items-center py-2 px-4 rounded-md border border-transparent shadow-sm bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        <TrashIcon
                          className="-ml-0.5 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                        削除
                      </button>
                    </Col>
                  </tr>
                </TableBody>
              </Table>
            </Card>
          ))
      )}

      <PhotoUpload
        isSlideOpen={isSlideOpen}
        updateSlide={setIsSlideOpen}
        selectedItem={selectedItem}
        result={result}
        turbineInspection={turbineInspection}
        photos={photos}
        setPhotos={setPhotos}
        selectedPhoto={selectedPhoto}
        setSelectedPhoto={setSelectedPhoto}
      />

      <EditCaption
        setIsEditCaptionOpen={setIsEditCaptionOpen}
        isEditCaptionOpen={isEditCaptionOpen}
        selectedPhoto={selectedPhoto}
        setSelectedPhoto={setSelectedPhoto}
      />

      <RemoveModal
        open={isRemoveModalOpen}
        setOpen={setIsRemoveModalOpen}
        remove={removePhoto}
      />
    </Layout>
  );
};

export default Photo;
